/**
 * Tenant Context for Subdomain-based Multi-tenancy
 * 
 * Detects tenant from:
 * 1. URL query parameter (?tenant=oldmutual) - for preview/testing
 * 2. Subdomain (oldmutual.cerebro.co.za) - for production
 * 
 * Provides:
 * - Tenant identification
 * - Branding configuration
 * - Feature flags
 * - SSO configuration
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config/api';

const API = API_BASE;

// Session storage key for tenant override
const TENANT_OVERRIDE_KEY = 'cerebro_tenant_override';

// Master platform defaults
const MASTER_TENANT = {
  isMaster: true,
  slug: null,
  orgId: null,
  name: 'Cerebro Core',
  branding: {
    logoUrl: 'https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png',
    primaryColor: '#D4AF37',
    secondaryColor: '#f59e0b',
    accentColor: '#0d9488',
    headingColor: '#ffffff',
    textColor: '#94a3b8',
    poweredByVisible: false
  },
  features: {
    courses: true,
    learning_labs: true,
    learning_pathways: true,
    masterclasses: true,
    ebooks: true,
    wsp: true,
    certificates: true,
    navi_coach: true,
    ai_course_generation: true,
    cpd_tracking: true,
    external_courses: true  // External content library (Old Mutual, etc.)
  },
  sso: {
    enabled: false,
    provider: null,
    loginUrl: null
  }
};

const TenantContext = createContext({
  tenant: MASTER_TENANT,
  loading: true,
  error: null,
  isTenant: false,
  tenantSlug: null,
  setTenantOverride: () => {},
  clearTenantOverride: () => {},
  refreshTenant: async () => {}
});

/**
 * Get tenant slug from URL query parameter
 */
function getTenantFromQuery() {
  const params = new URLSearchParams(window.location.search);
  return params.get('tenant');
}

/**
 * Get tenant slug from session storage (persists during navigation)
 */
function getTenantFromStorage() {
  try {
    return sessionStorage.getItem(TENANT_OVERRIDE_KEY);
  } catch {
    return null;
  }
}

/**
 * Save tenant slug to session storage
 */
function saveTenantToStorage(slug) {
  try {
    if (slug) {
      sessionStorage.setItem(TENANT_OVERRIDE_KEY, slug);
    } else {
      sessionStorage.removeItem(TENANT_OVERRIDE_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Extract tenant slug from current hostname (for Cerebro platform domains)
 */
export function extractTenantSlug() {
  const hostname = window.location.hostname.toLowerCase();
  
  // Master domains (no tenant)
  const masterDomains = [
    'localhost',
    '127.0.0.1',
    'cerebro.co.za',
    'www.cerebro.co.za',
    'admin.cerebro.co.za',
    'cerebro-lms.azurewebsites.net',
    'cerebro-lms-staging.azurewebsites.net',
    'cerebro.navigatecompliance.io'
  ];
  
  // Check if master domain
  if (masterDomains.includes(hostname)) {
    return null;
  }

  const reserved = ['www', 'admin', 'api', 'app', 'mail', 'cdn', 'dev', 'staging', 'test'];
  
  // Check for preview environment
  if (hostname.includes('preview.emergentagent.com') || hostname.includes('preview.emergentcf.cloud')) {
    // Check for tenant prefix with double-hyphen: tenant--appname.preview.emergentagent.com
    const parts = hostname.split('.');
    if (parts.length > 0 && parts[0].includes('--')) {
      const potentialSlug = parts[0].split('--')[0];
      if (!reserved.includes(potentialSlug)) {
        return potentialSlug;
      }
    }
    return null;
  }
  
  // Check for Cerebro platform subdomain (e.g. oldmutual.cerebro.co.za)
  if (hostname.endsWith('.cerebro.co.za')) {
    const sub = hostname.slice(0, -'.cerebro.co.za'.length);
    const parts = sub.split('.');
    const candidate = parts[parts.length - 1];
    if (candidate && !reserved.includes(candidate)) {
      return candidate;
    }
    return null;
  }

  // Check for local dev subdomain (e.g. oldmutual.localhost)
  if (hostname.endsWith('.localhost')) {
    const sub = hostname.slice(0, -'.localhost'.length);
    const parts = sub.split('.');
    const candidate = parts[parts.length - 1];
    if (candidate && !reserved.includes(candidate)) {
      return candidate;
    }
    return null;
  }
  
  // All other hostnames (e.g. aura.digitaldehari.com) are custom domains,
  // not Cerebro platform subdomains. They will be resolved via /api/tenant/by-host.
  return null;
}

/**
 * Determine tenant slug from all sources (priority order)
 */
function determineTenantSlug() {
  // 1. Check URL query parameter (highest priority - for testing/preview)
  const queryTenant = getTenantFromQuery();
  if (queryTenant) {
    saveTenantToStorage(queryTenant);
    return queryTenant;
  }
  
  // 2. Check session storage (persists during navigation)
  const storedTenant = getTenantFromStorage();
  if (storedTenant) {
    return storedTenant;
  }
  
  // 3. Check subdomain (production)
  return extractTenantSlug();
}

// Hosts that always mean the master platform, never a tenant.
const MASTER_HOSTS = [
  'localhost',
  '127.0.0.1',
  'cerebro.co.za',
  'www.cerebro.co.za',
  'admin.cerebro.co.za',
  'cerebro-lms.azurewebsites.net',
  'cerebro-lms-staging.azurewebsites.net',
  'cerebro.navigatecompliance.io',
];

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(MASTER_TENANT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantSlug, setTenantSlug] = useState(null);

  const normalizeAssetUrl = (url) => {
    if (!url) return null;
    if (typeof url === 'string' && url.includes('integrations.emergentagent.com/objstore/api/v1/storage/objects/')) {
      const remainder = url.split('integrations.emergentagent.com/objstore/api/v1/storage/objects/')[1];
      return `/api/public/files/${remainder}`;
    }
    return url;
  };

  const applyTenant = (data) => {
    setTenant({
      isMaster: false,
      slug: data.slug,
      orgId: data.org_id,
      name: data.name,
      branding: {
        logoUrl: normalizeAssetUrl(data.branding?.logo_url),
        primaryColor: data.branding?.primary_color || '#D4AF37',
        secondaryColor: data.branding?.secondary_color || '#f59e0b',
        accentColor: data.branding?.accent_color || '#0d9488',
        headingColor: data.branding?.heading_color || '#ffffff',
        textColor: data.branding?.text_color || '#94a3b8',
        faviconUrl: normalizeAssetUrl(data.branding?.favicon_url),
        loginBackgroundUrl: normalizeAssetUrl(data.branding?.login_background_url),
        landingPage: data.branding?.landing_page || {},
        poweredByVisible: true
      },
      features: data.features || MASTER_TENANT.features,
      sso: {
        enabled: data.sso?.enabled || false,
        provider: data.sso?.provider,
        loginUrl: data.sso?.login_url
      }
    });
  };

  const fetchTenant = async (slugOverride = null) => {
    setLoading(true);
    setError(null);
    
    const host = window.location.hostname.toLowerCase();
    const isMasterHost = MASTER_HOSTS.includes(host);
    const querySlug = getTenantFromQuery();
    const subdomainSlug = extractTenantSlug();

    // 1. Explicit slugOverride or query param (?tenant=...) prioritised for testing/admin
    if (slugOverride || querySlug) {
      const targetSlug = slugOverride || querySlug;
      setTenantSlug(targetSlug);
      try {
        const res = await axios.get(`${API}/api/tenant/by-slug/${targetSlug}`);
        applyTenant(res.data);
        saveTenantToStorage(targetSlug);
      } catch (err) {
        console.error('Failed to fetch tenant by slug:', err);
        setError(`Tenant '${targetSlug}' not found`);
        setTenant(MASTER_TENANT);
        saveTenantToStorage(null);
      } finally {
        setLoading(false);
      }
      return;
    }

    // 2. Custom domain lookup: if on a non-master, non-cerebro.co.za domain (e.g. aura.digitaldehari.com)
    // always query /api/tenant/by-host first so the tenant's branding and settings are applied.
    if (!isMasterHost && !subdomainSlug) {
      try {
        const res = await axios.get(`${API}/api/tenant/by-host`, { params: { host } });
        applyTenant(res.data);
        saveTenantToStorage(res.data.slug);
        setTenantSlug(res.data.slug);
        setLoading(false);
        return;
      } catch (err) {
        console.warn(`Host '${host}' not resolved to a custom domain tenant:`, err);
      }
    }

    // 3. Platform subdomain (e.g. oldmutual.cerebro.co.za) or stored tenant session
    const targetSlug = subdomainSlug || getTenantFromStorage();
    setTenantSlug(targetSlug);

    if (targetSlug) {
      try {
        const res = await axios.get(`${API}/api/tenant/by-slug/${targetSlug}`);
        applyTenant(res.data);
        saveTenantToStorage(targetSlug);
        setLoading(false);
        return;
      } catch (err) {
        console.error('Failed to fetch tenant by slug:', err);
        // Safety net: if not a master host, attempt by-host before defaulting to master
        if (!isMasterHost) {
          try {
            const res = await axios.get(`${API}/api/tenant/by-host`, { params: { host } });
            applyTenant(res.data);
            saveTenantToStorage(res.data.slug);
            setTenantSlug(res.data.slug);
            setLoading(false);
            return;
          } catch (byHostErr) {
            // Both lookups failed
          }
        }
        setError(`Tenant '${targetSlug}' not found`);
        setTenant(MASTER_TENANT);
        saveTenantToStorage(null);
        setLoading(false);
        return;
      }
    }

    // 4. Default to master platform
    setTenant(MASTER_TENANT);
    setLoading(false);
  };

  // Set tenant override manually (for admin switching)
  const setTenantOverride = (slug) => {
    saveTenantToStorage(slug);
    fetchTenant(slug);
  };

  // Clear tenant override (return to master)
  const clearTenantOverride = () => {
    saveTenantToStorage(null);
    setTenantSlug(null);
    setTenant(MASTER_TENANT);
  };

  useEffect(() => {
    fetchTenant();
  }, []);

  // Apply tenant branding to CSS variables
  useEffect(() => {
    if (tenant.branding?.primaryColor) {
      const primary = tenant.branding.primaryColor;
      document.documentElement.style.setProperty('--tenant-primary', primary);
      
      // Also set derived colors for consistent theming
      document.documentElement.style.setProperty('--tenant-primary-hover', adjustBrightness(primary, -15));
      document.documentElement.style.setProperty('--tenant-primary-light', adjustBrightness(primary, 30));
      document.documentElement.style.setProperty('--tenant-primary-10', `${primary}1A`);
      document.documentElement.style.setProperty('--tenant-primary-20', `${primary}33`);
    }

    if (tenant.branding?.secondaryColor) {
      const secondary = tenant.branding.secondaryColor;
      document.documentElement.style.setProperty('--tenant-secondary', secondary);
      document.documentElement.style.setProperty('--tenant-secondary-10', `${secondary}1A`);
      document.documentElement.style.setProperty('--tenant-secondary-20', `${secondary}33`);
    }

    if (tenant.branding?.accentColor) {
      const accent = tenant.branding.accentColor;
      document.documentElement.style.setProperty('--tenant-accent', accent);
      document.documentElement.style.setProperty('--tenant-accent-10', `${accent}1A`);
      document.documentElement.style.setProperty('--tenant-accent-20', `${accent}33`);
    }

    if (tenant.branding?.headingColor) {
      document.documentElement.style.setProperty('--tenant-heading', tenant.branding.headingColor);
    }

    if (tenant.branding?.textColor) {
      document.documentElement.style.setProperty('--tenant-text', tenant.branding.textColor);
    }
    
    // Update document title
    if (!tenant.isMaster && tenant.name) {
      document.title = `${tenant.name} | Learning Platform`;
    } else {
      document.title = 'Cerebro Core | A Workforce Capability Platform';
    }
    
    // Update favicon if provided
    if (tenant.branding?.faviconUrl) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = tenant.branding.faviconUrl;
      document.head.appendChild(link);
    }
  }, [tenant]);

  return (
    <TenantContext.Provider value={{
      tenant,
      loading,
      error,
      isTenant: !tenant.isMaster,
      tenantSlug,
      setTenantOverride,
      clearTenantOverride,
      refreshTenant: fetchTenant
    }}>
      {children}
    </TenantContext.Provider>
  );
}

// Helper: Adjust color brightness for derived colors
function adjustBrightness(hex, percent) {
  if (!hex) return hex;
  hex = hex.replace('#', '');
  
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const adjust = (val) => Math.max(0, Math.min(255, val + Math.round((percent / 100) * 255)));
  
  const newR = adjust(r).toString(16).padStart(2, '0');
  const newG = adjust(g).toString(16).padStart(2, '0');
  const newB = adjust(b).toString(16).padStart(2, '0');
  
  return `#${newR}${newG}${newB}`;
}

export function useTenant() {
  return useContext(TenantContext);
}

/**
 * Hook to check if a feature is enabled for the current tenant
 */
export function useTenantFeature(featureName) {
  const { tenant } = useTenant();
  return tenant.features?.[featureName] !== false;
}

/**
 * Component that only renders children if feature is enabled
 */
export function TenantFeature({ feature, children, fallback = null }) {
  const isEnabled = useTenantFeature(feature);
  return isEnabled ? children : fallback;
}

export default TenantContext;
